import React, { useState } from "react";
import BACK_IMG from "../common/back-img.png";
import Skeleton from "react-loading-skeleton";
import HeartBlackBorder from "../common/heart-black-border";
import { useNavigate } from "react-router-dom";
import { Store } from "../../__generated__/graphql";
import ResponsiveCDNImage from "../common/image/responsive-cdn-image";
import clsx from "clsx";

interface StoreHeaderProps {
  loading: boolean;
  store: Store | undefined;
  onClickLike: (storeId: number, isLiked: boolean | null) => Promise<void>;
}

const StoreHeader: React.FC<StoreHeaderProps> = ({
  loading,
  store,
  onClickLike,
}) => {
  const navigate = useNavigate();
  const [isImageLoading, setIsImageLoading] = useState(true);

  /**
   * 뒤로가기 버튼 클릭 시
   * - window.history.state.idx: 현재 페이지의 세션 히스토리 인덱스 (0부터 시작)
   * - react-router v6 부터 추가됨
   *     - length - (number) The number of entries in the history stack (https://v5.reactrouter.com/web/api/history)
   *
   * - 이전 경로가 무엇인지는 알 수 없지만, 인덱스는 알 수 있다.
   * - 이렇게 하면 푸시알림을 받고 바로 store 페이지로 이동한 경우, 뒤로가기 버튼 클릭 시 대응가능
   *
   * - idx > 0이면 이전 페이지가 존재한다는 뜻 -> navigate(-1)
   * - idx === 0이면 이전 페이지가 없다는 뜻 -> 홈("/")으로 이동
   */
  const handleBackClick = () => {
    const idx = (window.history.state && window.history.state?.idx) ?? 0;
    idx > 0 ? navigate(-1) : navigate("/");
  };

  return (
    <div className="relative w-full mx-auto bg-white overflow-hidden">
      <div className="relative w-full h-56 flex flex-col justify-center">
        <img
          src={BACK_IMG}
          onClick={handleBackClick}
          className="absolute top-5 left-5 w-[40px] h-[40px]"
          alt="back"
        />

        {isImageLoading && <Skeleton className="w-full h-56" />}

        <ResponsiveCDNImage
          className={clsx(
            "w-full h-56 object-cover rounded-b-3xl",
            isImageLoading ? "hidden opacity-0" : "opacity-100"
          )}
          alt="Store"
          src={store?.img as string}
          onLoad={() => setIsImageLoading(false)}
        />
      </div>

      <div className="px-4 pt-4">
        <div className="flex justify-between">
          {loading ? (
            <Skeleton style={{ height: 30, width: 80 }} />
          ) : (
            <div className="flex gap-1 items-end mr-4">
              <h2 className="text-3xl font-semibold text-black pb-0 line-clamp-1 leading-none">
                {store?.title}
              </h2>
              {store?.reviewCount && store?.reviewCount > 0 ? (
                <div className="text-sm text-gray-600 whitespace-nowrap leading-none">
                  (리뷰 {store?.reviewCount}개)
                </div>
              ) : null}
            </div>
          )}

          <div className="flex items-center">
            <HeartBlackBorder
              isLiked={store?.isLiked}
              onClick={async (e) => {
                e.stopPropagation();
                await onClickLike(
                  store?.id as number,
                  store?.isLiked as boolean
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreHeader;
